/**
 * Sankey Diagram Canvas Component
 *
 * Renders interactive Sankey diagram using @nivo/sankey library
 * Shows data flow from source files to Neo4j schema
 */

import React, { useState } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';
import { MappingVisualizationData } from '../../types/mapping';
import { SankeyData, getNodeLabel } from '../../lib/sankeyTransformers';

interface SankeyCanvasProps {
  data: SankeyData;
  mappingData: MappingVisualizationData | null;
  onNodeClick?: (nodeId: string) => void;
  onScaleReady?: (api: { zoomIn: () => void; zoomOut: () => void; resetZoom: () => void }) => void;
}

const SankeyCanvas: React.FC<SankeyCanvasProps> = ({ data, mappingData, onNodeClick, onScaleReady }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(0.5, scale * delta), 3);
    setScale(newScale);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: event.clientX - position.x, y: event.clientY - position.y });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev * 1.2, 3));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev * 0.8, 0.5));
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Expose zoom API to parent
  React.useEffect(() => {
    if (onScaleReady) {
      onScaleReady({ zoomIn, zoomOut, resetZoom });
    }
  }, [onScaleReady]);

  return (
    <div
      className="w-full h-full overflow-hidden"
      style={{ minHeight: '600px', cursor: isDragging ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.2s',
          width: '100%',
          height: '100%',
        }}
        onWheel={handleWheel}
      >
        <ResponsiveSankey
          data={data}
          margin={{ top: 40, right: 180, bottom: 40, left: 180 }}
          align="justify"
          colors={{ scheme: 'category10' }}
          nodeOpacity={1}
          nodeHoverOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={18}
          nodeSpacing={24}
          nodeBorderWidth={0}
          nodeBorderColor={{
            from: 'color',
            modifiers: [['darker', 0.8]],
          }}
          nodeBorderRadius={3}
          linkOpacity={0.5}
          linkHoverOpacity={0.8}
          linkHoverOthersOpacity={0.1}
          linkContract={3}
          linkBlendMode="multiply"
          enableLinkGradient={true}
          label={(node) => getNodeLabel(node.id as string, mappingData)}
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={16}
          labelTextColor={{
            from: 'color',
            modifiers: [['darker', 1]],
          }}
          onClick={(node: any) => {
            if (onNodeClick && node.id) {
              onNodeClick(node.id as string);
            }
          }}
        />
      </div>
    </div>
  );
};

export default SankeyCanvas;
